import { api } from './client'

/**
 * R5-01 (D-01) — Trattativa: preventivo fatto al cliente in attesa di esito, separata dalla pratica (la bozza è il
 * contratto non ancora inviato alla firma). Vinta crea la pratica in bozza precompilata. Attiva solo con il flag
 * `trattative.scheda`: spento, le API rispondono 404.
 */
export type DealStatus = 'Aperta' | 'Vinta' | 'Persa'

export const DEAL_STATUSES: { value: DealStatus; label: string }[] = [
  { value: 'Aperta', label: 'Aperta' },
  { value: 'Vinta', label: 'Vinta' },
  { value: 'Persa', label: 'Persa' },
]

export interface Deal {
  id: number
  title: string
  customerId: number | null
  leadId: number | null
  salesUserId: number
  departmentId: number | null
  productIds: number[]
  offerDescription: string | null
  quoteAmount: number | null
  quoteDate: string | null
  expiresOn: string | null
  probability: number | null
  status: DealStatus
  lostReason: string | null
  closedAt: string | null
  closedByUserId: number | null
  contractId: number | null
  notes: string | null
  createdAt: string
  updatedAt: string | null
  createdBy: number | null
  isExpired: boolean
}

export interface DealList {
  total: number
  page: number
  pageSize: number
  items: Deal[]
}

export interface DealBody {
  title: string
  customerId: number | null
  leadId: number | null
  salesUserId: number | null
  departmentId: number | null
  productIds: number[]
  offerDescription: string | null
  quoteAmount: number | null
  quoteDate: string | null
  expiresOn: string | null
  probability: number | null
  notes: string | null
}

export interface DealListQuery {
  status?: DealStatus | ''
  customerId?: number
  leadId?: number
  salesUserId?: number
  mine?: boolean
  expiredOnly?: boolean
  search?: string
  page?: number
  pageSize?: number
}

export interface DealWinResult {
  id: number
  status: DealStatus
  contractId: number
  referenceCode?: string | null
  productWarning?: string | null
  replayed?: boolean
}

export const dealsApi = {
  list: (q: DealListQuery = {}) => {
    const p = new URLSearchParams()
    if (q.status) p.set('status', q.status)
    if (q.customerId) p.set('customerId', String(q.customerId))
    if (q.leadId) p.set('leadId', String(q.leadId))
    if (q.salesUserId) p.set('salesUserId', String(q.salesUserId))
    if (q.mine) p.set('mine', 'true')
    if (q.expiredOnly) p.set('expiredOnly', 'true')
    if (q.search) p.set('search', q.search)
    p.set('page', String(q.page ?? 1))
    p.set('pageSize', String(q.pageSize ?? 50))
    return api<DealList>(`/api/deals?${p.toString()}`)
  },
  get: (id: number) => api<Deal>(`/api/deals/${id}`),
  create: (body: DealBody) => api<{ id: number }>('/api/deals', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: DealBody) => api<unknown>(`/api/deals/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  win: (id: number) => api<DealWinResult>(`/api/deals/${id}/win`, { method: 'POST', body: '{}' }),
  lose: (id: number, reason: string) =>
    api<unknown>(`/api/deals/${id}/lose`, { method: 'POST', body: JSON.stringify({ reason }) }),
  reopen: (id: number) => api<unknown>(`/api/deals/${id}/reopen`, { method: 'POST', body: '{}' }),
  remove: (id: number) => api<void>(`/api/deals/${id}`, { method: 'DELETE' }),
}

export function dealStatusTone(d: Pick<Deal, 'status' | 'isExpired'>): 'neutral' | 'primary' | 'success' | 'warning' | 'error' {
  if (d.status === 'Vinta') return 'success'
  if (d.status === 'Persa') return 'error'
  return d.isExpired ? 'warning' : 'primary'
}
